import React, {
  DependencyList,
  EffectCallback,
  useCallback,
  useEffect,
  useRef,
  useState,
  WheelEventHandler,
} from "react";
import { Box, Grid, Stack, Typography, useMediaQuery } from "@mui/material";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider, { Settings } from "react-slick";
import Slide from "./slide";
import cardSideLine from "../../assets/icons/slide-side-dot-line.svg";
import spanGroup from "../../assets/icons/span-group-2.gif";
import firstSlideImg from "../../assets/images/play-slide-first-img.png";
import secondSlideImg from "../../assets/images/play-slide-second-img.png";
import thirdSlideImg from "../../assets/images/play-slide-third-img.png";
import exesGroup from "../../assets/icons/white-exes-animated.gif";
import _ from "lodash";
import { SLIDES } from "../side-scroller/constants";

const sxStyle = {
  slideIntroduction: {
    display: "flex",
    justifyContent: { xs: "center", md: "flex-start" },
    alignItems: "center",
    paddingTop: { xs: "0px", md: "180px" },
    paddingLeft: { xs: "0px", md: "30px" },
    marginBottom: "50px",
  },
  introductionSlideText: {
    color: "white",
    fontFamily: "Dosis",
    fontSize: { xs: "42px", md: "64px" },
    lineHeight: { xs: "42px", md: "64px" },
    fontWeight: "700",
    textAlign: { xs: "center", md: "left" },
    marginRight: "15px",
  },
  introductionSlideP22: {
    color: "white",
    fontFamily: "p22-arts-and-crafts",
    fontSize: { xs: "42px", md: "64px" },
    lineHeight: { xs: "42px", md: "64px" },
    fontWeight: "700",
    textAlign: { xs: "center", md: "left" },
  },
  content: {
    display: "flex",
    backgroundColor: "black",
    width: { xs: "100%", lg: "1440px" },
    maxWidth: "1440px",
  },
};
export function useLazyEffect(
  effect: EffectCallback,
  deps: DependencyList = [],
  wait = 1600
) {
  const cleanUp = useRef<void | (() => void)>();
  const effectRef = useRef<EffectCallback>();
  const updatedEffect = useCallback(effect, deps);
  effectRef.current = updatedEffect;
  const lazyEffect = useCallback(
    _.debounce(() => {
      cleanUp.current = effectRef.current?.();
    }, wait),
    []
  );
  useEffect(lazyEffect, deps);
  useEffect(() => {
    return () => {
      cleanUp && cleanUp?.current && cleanUp?.current();
    };
  }, []);
}

const HowToPlaySection = () => {
  const matches = useMediaQuery("(max-width:600px)");
  const [preventScroll, setPreventScroll] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef<Slider>(null);

  let settings: Settings = {
    infinite: false,
    slidesToShow: 3,
    slidesToScroll: 1,
    initialSlide: 0,
    speed: 500,
    easing: "easeOutQuint",
    afterChange(currentSlide: number) {
      setCurrentSlide(currentSlide);
    },
    arrows: false,
  };

  if (matches) {
    settings = {
      infinite: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: 0,
      speed: 500,
      arrows: true,
      easing: "easeOutQuint",
    };
  }

  useEffect(() => {
    const preventDefault = (e: WheelEvent) => {
      const carouselNode = document.getElementById("how-to-play-slider");
      const carouselRect = carouselNode?.getBoundingClientRect();
      const viewportMiddle = window.innerHeight / 2;
      console.log(e.deltaY);
      const allowScrollDown = e.deltaY > 0 && currentSlide === 1;
      const allowScrollUp = e.deltaY < 0 && currentSlide === 0;
      // Check if the carousel is in the middle of the viewport
      if (
        carouselRect &&
        carouselRect.top < viewportMiddle &&
        carouselRect.bottom > viewportMiddle
      ) {
        // Prevent default scrolling behavior
        if (!allowScrollDown && !allowScrollUp) {
          e.preventDefault();
          setPreventScroll(true);
        }
      } else {
        setPreventScroll(false);
      }
    };
    window.addEventListener("wheel", preventDefault, { passive: false });
    return () => {
      window.removeEventListener("wheel", preventDefault);
    };
  }, [currentSlide, setPreventScroll]);

  const handleScroll: WheelEventHandler = useCallback(
    (event) => {
      if (preventScroll) {
        if (event.deltaY > 0) {
          carouselRef.current?.slickNext();
        } else if (event.deltaY < 0) {
          carouselRef.current?.slickPrev();
        }
      }
    },
    [preventScroll]
  );

  return (
    <Box
      sx={{
        backgroundColor: "black",
        paddingTop: { xs: "40px", md: "127px" },
        minHeight: "650px",
        maxHeight: { xs: "720px", md: "auto" },
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        overflow: "hidden",
        position: "relative",
        width: "100%",
      }}
      onWheel={handleScroll}
      id={"how-to-play-slider"}
    >
      <Stack sx={sxStyle.content}>
        <img
          src={spanGroup}
          style={{ position: "absolute", left: "30px", height: "120px" }}
        />
        <img
          src={exesGroup}
          style={{
            position: "absolute",
            left: "920px",
            top: "50px",
            width: "320px",
            height: "30px",
          }}
        />
        {matches ? (
          <Stack sx={{ marginBottom: "250px" }}>
            <Stack sx={sxStyle.slideIntroduction}>
              <Typography sx={sxStyle.introductionSlideText}>COMO</Typography>
              <Typography sx={sxStyle.introductionSlideText}>
                CONSEGUIR
              </Typography>
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: { xs: "center", md: "left" },
                }}
              >
                <Typography sx={sxStyle.introductionSlideText}>TUS</Typography>
                <Typography sx={sxStyle.introductionSlideP22}>CARDS</Typography>
              </Grid>
            </Stack>
            <Box
              sx={{
                position: "absolute",
                left: "800px",
                top: "12px",
                height: "650px",
              }}
            >
              <img src={cardSideLine} />
            </Box>
            <Slider {...settings} ref={carouselRef}>
              <Slide
                slideNumber={"01"}
                slideAction={"JUEGA"}
                slideImg={firstSlideImg}
                slideText={
                  "Recibe una card de forma gratuita todos los días participando en la trivia o en el minigame."
                }
              />
              <Slide
                slideNumber={"02"}
                slideAction={"COMPRA"}
                slideImg={secondSlideImg}
                slideText={
                  "Adquiere paquetes de cards para completar tus sessions y sagas" +
                  " antes que nadie para ganar premios y beneficios únicos."
                }
              />
              <Slide
                slideNumber={"03"}
                slideAction={"CANJEA"}
                slideImg={thirdSlideImg}
                slideText={
                  "Ingresa a una session, selecciona la card que quieras y ofrece una tuya para intercambiarla." +
                  "  Las cards se canjean únicamente por otras con el mismo nivel de dificultad" +
                  " (fáciles, intermedias y difíciles). "
                }
              />
            </Slider>
          </Stack>
        ) : (
          <Slider {...settings} ref={carouselRef}>
            <Stack sx={sxStyle.slideIntroduction}>
              <Typography sx={sxStyle.introductionSlideText}>COMO</Typography>
              <Typography sx={sxStyle.introductionSlideText}>
                CONSEGUIR
              </Typography>
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: { xs: "center", lg: "left" },
                }}
              >
                <Typography sx={sxStyle.introductionSlideText}>TUS</Typography>
                <Typography sx={sxStyle.introductionSlideP22}>CARDS</Typography>
              </Grid>
              <Box
                sx={{
                  position: "absolute",
                  left: "440px",
                  top: 0,
                  height: "650px",
                }}
              >
                <img src={cardSideLine} />
              </Box>
            </Stack>
            <Slide
              slideNumber={"01"}
              slideAction={"JUEGA"}
              slideImg={firstSlideImg}
              slideText={
                "Gana una Card Diaria gratis, respondiendo la Trivia y completando los minijuegos."
              }
            />
            <Slide
              slideNumber={"02"}
              slideAction={"COMPRA"}
              slideImg={secondSlideImg}
              slideText={
                "Compra tus Packs de Cards en \n" +
                "la tienda dentro del juego."
              }
            />
            <Slide
              slideNumber={"03"}
              slideAction={"CANJEA"}
              slideImg={thirdSlideImg}
              slideText={
                "Compra tus Packs de Cards en la \n" +
                "Tienda dentro del juego."
              }
            />
          </Slider>
        )}
        <img
          src={exesGroup}
          style={{ position: "absolute", left: "110px", top: "4863px" }}
        />
      </Stack>
    </Box>
  );
};

export default HowToPlaySection;
